<template>
    <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
            <p class="modal-card-title">Redirecting to Quiz Attempt</p>
        </header>

        <section class="modal-card-body">
            <p>{{ quizAttemptStatus }}</p>

            <p class="m-t-lg">You will be <strong>redirected</strong> in {{ countDown }}.</p>
        </section>

        <footer class="modal-card-foot">
            <e-button
                v-if="closing"
                label="Go Now"
                :loading="submitting"
                type="is-primary"
                @click="close"
                @keyup="close"
            />
            <e-button
                v-else
                label="Go Now"
                :loading="submitting"
                type="is-primary"
                @click="navigateToQuiz"
                @keyup="navigateToQuiz"
            />
        </footer>
    </div>
</template>

<script>
import isNull from 'lodash/isNull.js';
import { useI18n } from 'vue-i18n';

import QuizAttempt from '@/models/QuizAttempt.js';
import User from '@/models/User.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default {
    name: 'QuizAttemptRedirectModal',

    props: {
        status: {
            type: String,
            default: 'started',
        },

        quizAttemptId: {
            type: [Number, String],
            required: false,
            default: null,
        },

        closing: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    emits: ['close'],

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    data: () => ({
        countDown: 10,
        timer: 1000,
        interval: null,
        submitting: false,
        checks: ['in-waiting-room', 'ready-to-start', 'attempt-started', 'in-waiting-room-post'],
        quizAttempt: null,
    }),

    computed: {
        quizAttemptStatus() {
            return this.started
                ? this.translate('quiz_attempt_started', { status: 'started' })
                : this.translate('quiz_attempt_ended', { status: 'ended' });
        },

        started() {
            return this.checks.some(check => check === this.status);
        },

        fallbackAttemptId() {
            const idFromRequest = this.quizAttempt ? this.quizAttempt.id : null;

            return this.$route.params?.attemptId || this.quizAttemptId || idFromRequest;
        },
    },

    watch: {
        countDown(value) {
            if (value === 0) {
                if (this.closing) {
                    this.close();
                } else {
                    this.navigateToQuiz();
                }
            }
        },
    },

    async created() {
        if (this.quizAttemptId > 0 && !this.closing) {
            await this.fetchAttempt();
        }
    },

    mounted() {
        this.interval = setInterval(() => {
            this.countDown -= 1;
        }, this.timer);
    },

    methods: {
        async fetchAttempt() {
            await User.update({
                where: this.$store.state.Auth.user.id,
                data: { quiz_modal: null },
            });

            const queryBuilder = new QueryBuilder(`quiz-attempts/${this.quizAttemptId}`);
            queryBuilder.include('quizActivity.activity');

            const { response } = await QuizAttempt.api().get(queryBuilder.toUrl());

            this.quizAttempt = response.data.data;
        },

        async close() {
            this.submitting = true;

            clearInterval(this.interval);

            this.interval = null;

            this.$emit('close');
        },

        async navigateToQuiz() {
            this.submitting = true;

            clearInterval(this.interval);

            this.countDown = 10;

            this.interval = null;

            if (this.quizAttempt && this.started) {
                const attemptHydrated = this.$store.getters['QuizAttempt/has'](this.quizAttemptId);

                const hasModal =
                    attemptHydrated && this.$store.getters[`QuizAttempt/${this.quizAttemptId}/get`]('modal');

                if (hasModal && this.status === 'attempt-started') {
                    await this.$store.dispatch('QuizAttempt/launchQuiz', {
                        payload: {
                            activityId: this.$route.params.activityId,
                            courseId: this.$route.params.courseId,
                            quizVersionId: this.quizAttempt.quiz_version_id,
                            quizActivityId: this.quizAttempt.quiz_activity_id,
                            id: this.quizAttemptId,
                        },
                        attemptId: this.quizAttempt.id,
                        type: 'quiz',
                    });

                    await this.$store.dispatch(`QuizAttempt/${this.quizAttempt.quiz_version_id}/closeModal`);

                    return;
                }
            }

            if (this.quizAttempt && !isNull(this.quizAttempt?.end_time) && !this.status) {
                await this.$store.dispatch(`QuizAttempt/${this.quizAttempt.quiz_version_id}/endAndApplyStatus`, {
                    status: null,
                });

                const { href } = this.$router.resolve({
                    name: 'activity-view',
                    params: {
                        activityId: this.$route.params.activityId,
                        courseId: this.$route.params.courseId,
                    },
                });

                window.location.href = href;

                return;
            }

            if (this.quizAttempt?.quiz_activity?.activity) {
                const { href } = this.$router.resolve({
                    name: 'quiz-attempt',
                    params: {
                        activityId: this.quizAttempt.quiz_activity.activity.id,
                        courseId: this.quizAttempt.quiz_activity.activity.course_id,
                        id: this.quizAttempt.quiz_version_id,
                        attemptId: this.quizAttemptId,
                    },
                });

                window.location.href = href;
                return;
            }

            window.location.reload();
        },
    },
};
</script>
